import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import { getMachhaAsset, AssetImg } from '../../lib/machhaAsset';
import styles from './CountUpStat.module.css';

interface CountUpStatProps {
  value: string;
  numericEnd?: number;
  suffix?: string;
  label: string;
  asset?: string;
  emoji?: string;
  color: string;
}

export function CountUpStat({ value, numericEnd, suffix = '', label, asset, emoji, color }: CountUpStatProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView || numericEnd === undefined) return;
    const duration = 1200;
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const p = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - p, 2); // ease-out quad
      setCount(Math.round(eased * numericEnd));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, numericEnd]);

  const display = numericEnd !== undefined ? `${count}${suffix}` : value;

  return (
    <motion.div
      ref={ref}
      className={styles.root}
      whileHover={{ y: -4 }}
      transition={{ type: 'spring', stiffness: 400, damping: 20 }}
    >
      <div className={styles['asset-wrap']}>
        {emoji ? (
          <div className={styles['emoji-icon']}>{emoji}</div>
        ) : (
          <AssetImg
            src={getMachhaAsset('LandingPageAssets', asset!)}
            alt={label}
            className={styles.asset}
          />
        )}
      </div>
      <div className={`${styles.value} ${color}`}>{display}</div>
      <div className={styles.label}>{label}</div>
    </motion.div>
  );
}
